'use client';

import { createLogger } from '@ctrl-enter/tools/utils';
import '@radix-ui/themes/styles.css';
import { UserProfile, WorkOsWidgets } from '@workos-inc/widgets';
import '@workos-inc/widgets/styles.css';
import { Eye, FileSignature, LoaderCircle, LogOut, User } from 'lucide-react';
import * as React from 'react';
import { useAuth } from '../auth';
import { useBAASigning } from '../hooks/use-baa-signing';
import { useCheckChatAccessQuery } from '../hooks/use-gateway-queries';
import { useWidgetToken } from '../hooks/use-widget-token';
import { useToken, useTokenError, useTokenLoading } from '../stores/widget-token-store';
import '../styles/workos-overrides.css';
import { Button } from './button';
import { InfoSection } from './info-section';
import { OpsConsoleLink } from './ops-console-link';
import { LoginRegisterPanel } from './login-register-panel';
import { RefreshUserButton } from './refresh-user-button';
import { WorkOsWidgetErrorBoundary } from './workos-widget-error-boundary';

const logger = createLogger('web:component:account-panel');

export type AccountPanelProps = {
  /** Additional CSS classes */
  className?: string;
  /** Callback when user clicks sign out */
  onSignOut?: () => void;
};

/**
 * AccountPanel component that displays user account settings including
 * profile picture, name, connected accounts, and account deletion options.
 * Follows the flat design system with no shadows or gradients.
 *
 * Uses automatic token lifecycle management with:
 * - Proactive refresh before expiry (T-5 minutes)
 * - Re-validation on page visibility/focus
 * - Automatic recovery from 403 errors
 */
const AccountPanel = React.forwardRef<HTMLDivElement, AccountPanelProps>(
  ({ className, onSignOut }, ref) => {
    const { isAuthenticated, logout, user } = useAuth();

    // Check if user is organization admin
    const isUserOrganizationAdmin = user?.role === 'admin';

    // Get subscription data to check BAA status
    const { data: chatAccessData } = useCheckChatAccessQuery(
      isAuthenticated && !!user?.organizationId
    );
    const subscriptionData = chatAccessData?.body?.subscription;

    // Determine if BAA needs to be signed (status is 'pending' means has subscription but needs BAA)
    const needsBAASignature = subscriptionData?.status === 'pending';

    const handleSignOut = async () => {
      await logout();
      onSignOut?.();
    };

    // Initialize token lifecycle management (runs in background)
    const { refresh } = useWidgetToken({
      refreshBeforeMinutes: 5,
      refreshOnVisibility: true,
      enableLogging: true, // Enable for debugging during initial rollout
    });

    // Get token state from centralized store
    const token = useToken();
    const isLoading = useTokenLoading();
    const error = useTokenError();

    // BAA signing/viewing functionality
    const {
      openBAASession,
      isLoading: isBAALoading,
      isPolling: isBAAPolling,
    } = useBAASigning({
      viewIfSigned: true, // Allow viewing if already signed
      showToasts: true,
    });

    // Debug: Log token state changes (MUST be before any conditional returns)
    React.useEffect(() => {
      logger.info(
        '🔵 [ACCOUNT-PANEL] Token state changed:',
        JSON.stringify(
          {
            hasToken: !!token,
            tokenLength: token?.length || 0,
            isLoading,
            hasError: !!error,
            errorMessage: error?.message,
            isAuthenticated,
          },
          null,
          2
        )
      );
    }, [token, isLoading, error, isAuthenticated]);

    // Debug: Log when rendering widget (MUST be before any conditional returns)
    React.useEffect(() => {
      if (token) {
        logger.info(
          '🟢 [ACCOUNT-PANEL] Rendering UserProfile widget with token:',
          JSON.stringify(
            {
              tokenLength: token.length,
              tokenPreview: `${token.substring(0, 50)}...${token.substring(token.length - 20)}`,
            },
            null,
            2
          )
        );
      }
    }, [token]);

    // Show login panel if user is not authenticated
    if (!isAuthenticated) {
      return (
        <LoginRegisterPanel
          ref={ref}
          className={className || undefined}
          title="Account Settings"
          description="Please sign in to manage your account settings"
        />
      );
    }

    // Show loading state
    if (isLoading) {
      return (
        <div ref={ref} className={className}>
          <InfoSection>
            <div className="flex items-center justify-center p-4">
              <div className="text-muted-foreground">Loading account settings...</div>
            </div>
          </InfoSection>
        </div>
      );
    }

    // Show error state with retry option
    if (error && !token) {
      return (
        <div ref={ref} className={className}>
          <InfoSection>
            <div className="flex flex-col items-center justify-center space-y-4">
              <div className="text-muted-foreground text-destructive">
                Failed to load account settings: {error.message}
              </div>
              <Button onClick={() => refresh()} variant="primary" size="md">
                Retry
              </Button>
            </div>
          </InfoSection>
        </div>
      );
    }

    // Don't render UserProfile until we have a token
    if (!token) {
      return null;
    }

    return (
      <div ref={ref} className={className}>
        {/* Staff-only: appears only for allowlisted internal admins */}
        <div className="mb-4 flex justify-end">
          <OpsConsoleLink />
        </div>
        <InfoSection title="Account" titleIcon={<User className="w-5 h-5" />}>
          <p className="text-muted-foreground -mt-2 mb-4">
            Manage your profile and account settings.
          </p>
          <WorkOsWidgetErrorBoundary
            onRetry={() => {
              logger.info('🔄 [ACCOUNT-PANEL] Error boundary triggered token refresh');
              refresh();
            }}
          >
            <WorkOsWidgets
              theme={{
                hasBackground: false,
                fontFamily: 'Calibre',
              }}
            >
              <UserProfile authToken={token} />
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mt-6 sm:justify-end">
                <RefreshUserButton className="w-full sm:w-auto" />
                {isUserOrganizationAdmin && (
                  <Button
                    onClick={openBAASession}
                    variant="secondary"
                    size="md"
                    className="flex items-center justify-center gap-2 w-full sm:w-auto"
                    disabled={isBAALoading || isBAAPolling}
                  >
                    {isBAALoading || isBAAPolling ? (
                      <>
                        <LoaderCircle className="w-4 h-4 animate-spin" />
                        {isBAAPolling ? 'Checking signature...' : 'Loading...'}
                      </>
                    ) : needsBAASignature ? (
                      <>
                        <FileSignature className="h-4 w-4" />
                        Sign BAA
                      </>
                    ) : (
                      <>
                        <Eye className="h-4 w-4" />
                        View BAA
                      </>
                    )}
                  </Button>
                )}
                <Button
                  onClick={handleSignOut}
                  variant="destructive"
                  size="md"
                  className="flex items-center justify-center gap-2 w-full sm:w-auto"
                >
                  <LogOut className="h-4 w-4" />
                  Sign Out
                </Button>
              </div>
            </WorkOsWidgets>
          </WorkOsWidgetErrorBoundary>
        </InfoSection>
      </div>
    );
  }
);

AccountPanel.displayName = 'AccountPanel';

export { AccountPanel };
